import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';

import {
  BehaviorSubject,
  catchError,
  filter,
  switchMap,
  take,
  throwError,
  Observable,
  finalize
} from 'rxjs';

import { LoginService } from '../../authentication/services/login.service';
import { CryptoJsService } from '../services/crypto-js.service';
import { NotificationService } from '../services/notification.service';
import { NgxSpinnerService } from 'ngx-spinner';

@Injectable()
export class BearerInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private localStorage: CryptoJsService,
    private loginService: LoginService,
    private notificationService: NotificationService,
    private _spinner: NgxSpinnerService
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.localStorage.getItem('token');
    //console.log('BearerInterceptor - token:', token);

    // Agregar token si existe
    let authReq = request;
    if (token) {
      authReq = this.addToken(request, token);
    }

    return next.handle(authReq).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 401) {

          console.log('error:', error.error.error);
          let errorMsgHeader = 'Sesión expirada';
          let errorMessage = 'Tu sesión ha expirado. Por favor, inicia sesión de nuevo.';

          if (error.error.error === 'invalid_grant') {

            errorMsgHeader = 'Credenciales incorrectas';
            errorMessage = 'El usuario o la contraseña son incorrectos.';

          }


          const accessToken = this.localStorage.getItem('token');
          const refreshToken = this.localStorage.getItem('refresh_token');
          //console.log('refreshToken in interceptor:', refreshToken);
          // Si NO hay refresh token o ya expiró → sesión muerta → logout
          if (!refreshToken || this.isTokenExpired(refreshToken)) {
            this.notificationService.okDialogSwal(
              errorMsgHeader,
              errorMessage,
            ).then(() => {
              this._spinner.hide();
              this.doLogout();
            });

            return throwError(() => error);
          }

          // Si el access token está expirado → intentamos refrescar
          if (accessToken && this.isTokenExpired(accessToken)) {
            return this.handle401Error(authReq, next, refreshToken);
          }

          // Si llegamos aquí: 401 pero el token parece no estar expirado (revocado, etc.) → logout
          this._spinner.hide();
          this.doLogout();
        }

        return throwError(() => error);
      })
    );
  }

  /*************************************
   * AGREGA EL TOKEN A LA PETICIÓN
   *************************************/
  private addToken(req: HttpRequest<any>, token: string) {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  /*************************************
   * MANEJO DEL REFRESH TOKEN EN 401
   *************************************/
  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
    refreshToken: string
  ): Observable<HttpEvent<any>> {

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.loginService.refreshToken(refreshToken).pipe(
        switchMap((resp: any) => {
          const newAccessToken = resp.access_token;
          const newRefreshToken = resp.refresh_token;

          // Guardamos los nuevos tokens
          this.localStorage.setItem('token', newAccessToken);
          this.localStorage.setItem('refresh_token', newRefreshToken);

          this.isRefreshing = false;
          this.refreshTokenSubject.next(newAccessToken);

          // Reintentamos la petición original con el nuevo token
          return next.handle(this.addToken(request, newAccessToken));
        }),

        catchError(err => {
          // Si el refresh falla (refresh inválido / expirado / sesión cerrada) → logout
          this.isRefreshing = false;
          this.doLogout();
          return throwError(() => err);
        })
      );

    } else {
      // Si YA se está refrescando → las demás peticiones esperan
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next.handle(this.addToken(request, token!)))
      );
    }
  }

  /*************************************
   * VERIFICA EXPIRACIÓN DE TOKEN (access o refresh)
   *************************************/
  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // exp en segundos → milisegundos
      return Date.now() >= exp;
    } catch {
      // Si no se puede decodificar, lo tratamos como expirado
      return true;
    }
  }

  /*************************************
   * LOGOUT GLOBAL
   *************************************/
  private doLogout(): void {
    this.loginService.logout();
  }
}
